import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Ban, Loader2 } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR, formatDate, methodLabel } from '../lib/format';
import { MoneyInput } from './MoneyInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const KAS_SOURCE_L = {
  cash: 'Tunai (di laci)',
  bank_ops: 'Rekening Operasional',
  bank_utama: 'Rekening Bisnis Utama',
};

const ALASAN = [
  'Customer berubah pikiran',
  'Acara dibatalkan',
  'Pindah tanggal',
  'Salah input',
  'Stok bermasalah',
  'Lainnya',
];

const KIND_L = { dp: 'DP', deposit: 'Uang Jaminan', pelunasan: 'Pelunasan', pembayaran: 'Pembayaran' };

const METHOD_TO_SOURCE = { cash: 'cash', transfer: 'bank_ops', qris: 'bank_ops' };

/**
 * Dialog pembatalan order.
 *
 * Nasib uang yang sudah masuk TIDAK dipaksa oleh aplikasi — diputuskan di sini,
 * per kasus. Pilihan bawaannya "hangus semua" karena itu aturan normal usaha.
 */
export default function CancelOrderDialog({ tx, open, onClose, onDone }) {
  const [pays, setPays] = useState(null);
  const [mode, setMode] = useState('all_forfeit');
  const [hangus, setHangus] = useState('');
  const [alasan, setAlasan] = useState(ALASAN[0]);
  const [catatan, setCatatan] = useState('');
  const [sumber, setSumber] = useState('cash');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !tx?.id) return;
    setPays(null);
    setMode('all_forfeit');
    setHangus('');
    setAlasan(ALASAN[0]);
    setCatatan('');
    setSaving(false);
    api.get(`/transactions/${tx.id}`)
      .then((r) => {
        const aktif = (r.data.payments || []).filter((p) => !p.voided);
        setPays(aktif);
        const terakhir = aktif[aktif.length - 1];
        setSumber(METHOD_TO_SOURCE[terakhir?.method] || 'cash');
      })
      .catch((err) => {
        setPays([]);
        toast.error(apiError(err, 'Gagal memuat rincian pembayaran'));
      });
  }, [open, tx?.id]);

  const masuk = (pays || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const nHangus = mode === 'all_forfeit' ? masuk : mode === 'all_refund' ? 0 : Number(hangus || 0);
  const nKembali = Math.max(0, masuk - nHangus);
  const lebih = mode === 'partial' && nHangus > masuk;

  const submit = async () => {
    if (lebih) return;
    setSaving(true);
    try {
      await api.post(`/transactions/${tx.id}/cancel`, {
        mode,
        forfeit_amount: mode === 'partial' ? nHangus : null,
        reason: alasan === 'Lainnya' ? (catatan.trim() ? 'Lainnya' : '') : alasan,
        reason_note: catatan.trim(),
        refund_source: nKembali >= 1 ? sumber : null,
      });
      if (masuk <= 0) toast.success(`Order ${tx.transaction_number} dibatalkan`);
      else if (nKembali < 1) toast.success(`Order dibatalkan — DP ${formatIDR(nHangus)} hangus, tercatat sebagai pemasukan`);
      else if (nHangus < 1) toast.success(`Order dibatalkan — DP ${formatIDR(nKembali)} dikembalikan ke customer`);
      else toast.success(`Order dibatalkan — ${formatIDR(nHangus)} hangus, ${formatIDR(nKembali)} dikembalikan`);
      onClose?.();
      onDone?.();
    } catch (err) {
      toast.error(apiError(err, 'Gagal membatalkan order'));
    } finally {
      setSaving(false);
    }
  };

  const Pilihan = ({ v, judul, ket }) => (
    <label
      data-testid={`cancel-mode-${v}`}
      className={`flex gap-2.5 items-start rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
        mode === v ? 'border-brown bg-brown/5' : 'border-border bg-white hover:bg-ivory'
      }`}
    >
      <input type="radio" name="cancel-mode" value={v} checked={mode === v}
        onChange={() => setMode(v)} className="mt-1 accent-brown" />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{judul}</span>
        {ket && <span className="block text-[11px] text-mocha mt-0.5">{ket}</span>}
      </span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="cancel-order-dialog" className="bg-ivory max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-danger">
            Batalkan Order {tx?.transaction_number}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-mocha -mt-1">
          {tx?.customer_name} • nilai order {formatIDR(tx?.total || 0)}
        </p>

        {pays === null ? (
          <p className="text-sm text-mocha py-6 text-center">Memuat rincian pembayaran…</p>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-white p-3" data-testid="cancel-payments-box">
              <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Uang yang sudah masuk</p>
              {masuk <= 0 ? (
                <p className="text-sm text-mocha mt-1" data-testid="cancel-no-payment">
                  Belum ada pembayaran — tidak ada uang yang perlu diurus.
                </p>
              ) : (
                <>
                  <p className="font-display text-xl text-ink mt-0.5" data-testid="cancel-paid-total">{formatIDR(masuk)}</p>
                  <div className="mt-2 space-y-1">
                    {pays.map((p) => (
                      <p key={p.id} className="text-[11px] text-mocha flex justify-between gap-2">
                        <span>{formatDate(p.date)} • {KIND_L[p.kind] || 'Pembayaran'} • {methodLabel(p.method)}</span>
                        <span className="font-semibold text-ink whitespace-nowrap">{formatIDR(p.amount)}</span>
                      </p>
                    ))}
                  </div>
                </>
              )}
            </div>

            {masuk > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">
                  Yang hangus jadi pemasukan
                </p>
                <Pilihan v="all_forfeit" judul={`Semua — ${formatIDR(masuk)}`}
                  ket="Aturan biasa: produk sudah ditahan untuk customer ini." />
                <Pilihan v="all_refund" judul="Tidak ada, dikembalikan semua"
                  ket={`${formatIDR(masuk)} keluar dari kas.`} />
                <Pilihan v="partial" judul="Sebagian" ket="Tentukan sendiri berapa yang hangus." />

                {mode === 'partial' && (
                  <div className="rounded-xl border border-border bg-white p-3 space-y-1.5">
                    <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">
                      Nominal yang hangus (Rp)
                    </label>
                    <MoneyInput
                      data-testid="cancel-forfeit-amount"
                      value={hangus}
                      onChange={setHangus}
                      className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30 ${
                        lebih ? 'border-danger' : 'border-input'
                      }`}
                    />
                    {lebih ? (
                      <p className="text-[11px] text-danger font-semibold" data-testid="cancel-forfeit-error">
                        Lebih besar dari uang yang sudah masuk ({formatIDR(masuk)}).
                      </p>
                    ) : (
                      <p className="text-[11px] text-mocha" data-testid="cancel-split-note">
                        Hangus {formatIDR(nHangus)} • dikembalikan {formatIDR(nKembali)}
                      </p>
                    )}
                  </div>
                )}

                {nKembali >= 1 && (
                  <div className="rounded-xl border border-border bg-white p-3 space-y-1.5">
                    <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">
                      Uang dikembalikan dari
                    </label>
                    <select
                      data-testid="cancel-refund-source"
                      value={sumber}
                      onChange={(e) => setSumber(e.target.value)}
                      className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
                    >
                      {Object.entries(KAS_SOURCE_L).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Alasan pembatalan</label>
              <select
                data-testid="cancel-reason"
                value={alasan}
                onChange={(e) => setAlasan(e.target.value)}
                className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
              >
                {ALASAN.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <input
                data-testid="cancel-reason-note"
                value={catatan}
                onChange={(e) => setCatatan(e.target.value)}
                placeholder={alasan === 'Lainnya' ? 'Tulis alasannya…' : 'Catatan tambahan (boleh kosong)'}
                className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
              />
            </div>

            <p className="text-[11px] text-mocha">
              Stok langsung terbuka kembali. Order tetap tersimpan di filter <b>Dibatalkan</b> beserta alasannya.
            </p>

            <div className="flex gap-2 justify-end pt-1">
              <button
                data-testid="cancel-order-no"
                onClick={() => onClose?.()}
                className="rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-semibold text-mocha hover:bg-ivory transition-colors"
              >
                Jangan
              </button>
              <button
                data-testid="cancel-order-yes"
                onClick={submit}
                disabled={saving || lebih}
                className="inline-flex items-center gap-2 rounded-xl bg-danger text-white px-5 py-2.5 text-sm font-semibold hover:bg-danger/90 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />}
                Batalkan Order
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
